import omit from 'lodash/omit';
import { action, observable, makeObservable } from 'mobx';
import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import HttpStatusCode from '@utils/httpStatusCodes';
import { AxiosResponse } from 'axios';
import { Region } from '@components/common/constants';
import { CatchErr } from '@utils/types';

export interface ISettingsStore {
  rootStore: IRootStore;
  tokens: ITokenInfo[] | undefined;
  getTokens: () => Promise<void>;
  createToken: (at: ITokenInfo) => Promise<string>;
  createTokenOrError: (at: ITokenInfo) => Promise<CreateTokenResult>;
  deleteToken: (name: string, workspaceId: string) => Promise<boolean>;
  reset(): void;
}

export interface ITokenInfo {
  name: string;
  role: string;
  description?: string;
  workspaceId: string;
}

interface IGetTokensResp extends ITokenInfo {
  userId: string;
}

interface ICreateTokenResp {
  success: boolean;
  data: { token: string };
}

/** The new token, or the request's error, so a form can say why there is no token. */
type CreateTokenResult =
  | { token: string; error?: undefined }
  | { token?: undefined; error?: CatchErr };

export class SettingsStore implements ISettingsStore {
  @observable public rootStore: IRootStore;

  @observable public tokens: ITokenInfo[] | undefined;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public async getTokens(): Promise<void> {
    const { organizationStore } = this.rootStore;
    try {
      const resp: AxiosResponse<IGetTokensResp[]> = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get(`/organizations/${organizationStore.id}/accessTokens`);
      if (resp.status === HttpStatusCode.Ok) {
        this.tokens = resp.data.map((token: IGetTokensResp) => omit(token, 'userId'));
      }
    } catch (error) {}
  }

  /** Returns the token, or an empty string on any failure. */
  @action.bound
  public async createToken(tokenInfo: ITokenInfo): Promise<string> {
    const { token } = await this.createTokenOrError(tokenInfo);
    return token ?? '';
  }

  /** Like createToken, but hands back the error, whose response carries the API's reason. */
  @action.bound
  public async createTokenOrError(tokenInfo: ITokenInfo): Promise<CreateTokenResult> {
    try {
      const resp: AxiosResponse<ICreateTokenResp> = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).post(`/workspaces/${tokenInfo.workspaceId}/accessTokens`, {
        role: tokenInfo.role,
        name: tokenInfo.name,
      });
      if (resp.status === HttpStatusCode.Ok) {
        return { token: resp.data.data.token };
      }
      return {};
    } catch (error) {
      return { error };
    }
  }

  @action.bound
  public async deleteToken(tokenName: string, workspaceId: string): Promise<boolean> {
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).delete(
        `/workspaces/${workspaceId}/accessTokens?name=${tokenName}`,
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  @action.bound
  public async reset() {
    this.tokens = undefined;
  }
}
